import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg';
import { Button } from '@dextech-ai/dex-design-system';
import Input from "@/components/Input/index";
import googleKeys from "@/constants/configuration/google-keys.json";
import countries from "@/constants/country";
import industries from "@/constants/industry";
import { NavLink } from "@/lib/router-dom";
import API_LOGIN from "@/services/api/login";
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import { filterByLabel, renderValueOptions } from "@/utils/select-items";
import { validateDomain, validateEmail } from "@/utils/validate-email.js";
import validateMobile from "@/utils/validate-mobile.js";
import { DEX_TYPE } from "@/config/constants";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Box, Select, SelectContent } from "@dextech-ai/dex-design-system";

const TradexSignup = ({
	register,
	errors,
	handleKeyPress,
	trigger,
	setCountry,
	setIndustry,
	isValid,
	loading,
}) => {
	const { env, dex } = getEnvAndDex();
	const defaultCaptchaValue = googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY;
	const [captchaToken, setCaptchaToken] = useState(!defaultCaptchaValue);
	const [errorMessage, setErrorMessage] = useState();
	const captchaRef = useRef(null);

	async function handleChangeReCaptcha(value) {
		const [error] = await API_LOGIN.siteVerify({ token: value });
		if (error) {
			setCaptchaToken(false);
			if (captchaRef.current) {
				captchaRef.current.reset();
			}
			return setErrorMessage("reCAPTCHA verification failed");
		}
		setCaptchaToken(true);
	}

	return (
		<div className="login-box__form__cols">
			<div className={`login-box__form__cols--flex`}>
				<div className="login-box__form__col">
					<Input
						name="firstName"
						label="First Name*"
						register={register({
							required: "This field is mandatory",
						})}
						placeholder="Enter name"
						error={errors.firstName}
						errorMessage={errors.firstName?.message}
						onBlur={(e) => (e.target.value = e.target.value.trim())}
						data-testid="first-name-input"
					/>
				</div>
				<div className="login-box__form__col">
					<Input
						name="lastName"
						label="Last Name*"
						register={register({
							required: "This field is mandatory",
						})}
						placeholder="Enter name"
						error={errors.lastName}
						errorMessage={errors.lastName?.message}
						onBlur={(e) => (e.target.value = e.target.value.trim())}
						data-testid="last-name-input"
					/>
				</div>
				<div className="login-box__form__col">
					<Input
						name="email"
						label="Company Email Address*"
						register={register({
							required: "This field is mandatory",
							validate: {
								checkEmail: (v) =>
									validateEmail(v) ||
									"Please enter a valid company email address",
								checkDomain: (v) =>
									validateDomain(v, DEX_TYPE.SGTRADEX) ||
									"The following domain are not allowed to be registered",
							},
						})}
						placeholder="Enter company email"
						error={errors.email}
						errorMessage={errors.email?.message}
						onBlur={(e) => (e.target.value = e.target.value.trim())}
						autoComplete="off"
						data-testid="email-input"
					/>
				</div>
				<div className="login-box__form__col">
					<Input
						name="mobileNo"
						label="Mobile Number"
						register={register({
							required: false,
							minLength: {
								value: 6,
								message:
									"Please enter valid mobile number ranging between 6-15 digits",
							},
							maxLength: {
								value: 15,
								message:
									"Please enter valid mobile number ranging between 6-15 digits",
							},
							validate: {
								checkMobileNumber: (v) =>
									validateMobile(v) ||
									"Please enter a valid number",
							},
						})}
						placeholder="Enter number"
						error={errors.mobileNo}
						errorMessage={errors.mobileNo?.message}
						onBlur={(e) => (e.target.value = e.target.value.trim())}
						onKeyPress={(e) => handleKeyPress(e)}
						autoComplete="off"
						data-testid="mobile-no-input"
					/>
				</div>
			</div>
			<div className={"login-box__form__cols--flex"}>
				<div className="login-box__form__col">
					<Input
						name="company"
						label="Company Name*"
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid company name",
							},
						})}
						placeholder="Enter company name"
						error={errors.company}
						errorMessage={errors.company?.message}
						onBlur={(e) => (e.target.value = e.target.value.trim())}
						data-testid="company-input"
					/>
				</div>
				<div className="login-box__form__col">
					<div className="input-field">
						<div className="input-field__label input-field__label--margin-bottom">
							Country
						</div>
						<Select
							placeholder="Select Country"
							showSearch
							filterOption={filterByLabel}
							onChange={(data) => {
								trigger("uen");
								setCountry(data);
							}}
							data-testid="country-dropdown"
						>
							<SelectContent>{renderValueOptions(countries)}</SelectContent>
						</Select>
					</div>
				</div>
				<div className="login-box__form__col">
					<div className="input-field">
						<div className="input-field__label input-field__label--margin-bottom">
							Industry
						</div>
						<Select
							placeholder="Select industry"
							onChange={(data) => setIndustry(data)}
							data-testid="industry-dropdown"
						>
							<SelectContent>{renderValueOptions(industries)}</SelectContent>
						</Select>
					</div>
				</div>
				{isValid && googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY && (
					<ReCAPTCHA
						sitekey={`${googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY}`}
						ref={captchaRef}
						style={{ marginTop: "20px" }}
						onChange={(value) =>
							handleChangeReCaptcha(value)
						}
						onExpired={() => setCaptchaToken(false)}
						size="normal"
						data-testid="recaptcha-test"
					/>
				)}
				{errorMessage && (
					<div className="login-box__form__col--error">
						<Error />{" "}
						<span className="t--required">
							{errorMessage}
						</span>
					</div>
				)}
				<div className="login-box__form__actions">
					<Button
						type="submit"
						id="sign-up-submit-trigger-id"
						fullWidth
						style={{ maxWidth: '168px' }}
						value="Submit"
						disabled={!isValid || !captchaToken}
						loading={loading}
						iconOnly={loading}
						aria-label={loading ? 'Signing up' : undefined}
						data-testid="sign-up-submit"
					>Sign Up</Button>

					<Box mt={6}>
						Already have an account?{" "}
						<NavLink className="link" to="/login">
							Log in
						</NavLink>{" "}
						instead.
					</Box>
				</div>
			</div>
		</div>
	);
};

export default TradexSignup;


TradexSignup.propTypes = {
	register: PropTypes.func.isRequired,
	errors: PropTypes.objectOf(
		PropTypes.shape({
			message: PropTypes.string,
		})
	).isRequired,
	handleKeyPress: PropTypes.func.isRequired,
	trigger: PropTypes.func.isRequired,
	setCountry: PropTypes.func.isRequired,
	setIndustry: PropTypes.func.isRequired,
	isValid: PropTypes.bool.isRequired,
	loading: PropTypes.bool.isRequired,
};